import { useEffect, useState } from "react";
import { Scale, Check, X, CircleDollarSign, Minus } from "lucide-react";
import api from "../lib/api";

/* Airline Fare-Attributes Comparison — what each fare class really includes. */
const STATUS = {
  yes: { icon: Check, cls: "text-emerald-600", bg: "bg-emerald-50" },
  fee: { icon: CircleDollarSign, cls: "text-amber-600", bg: "bg-amber-50" },
  no: { icon: X, cls: "text-rose-600", bg: "bg-rose-50" },
  part: { icon: Minus, cls: "text-zinc-500", bg: "bg-zinc-50" },
};

const Cell = ({ a }) => {
  const S = STATUS[a?.s] || STATUS.part;
  const I = S.icon;
  return (
    <td className={`px-2 py-2 text-xs align-top ${S.bg}`}>
      <span className={`inline-flex items-start gap-1 ${S.cls}`}>
        <I className="w-3.5 h-3.5 mt-[1px] shrink-0" />
        <span className="text-zinc-700">{a?.t || "—"}</span>
      </span>
    </td>
  );
};

export default function FareComparison() {
  const [airlines, setAirlines] = useState([]);
  const [attrs, setAttrs] = useState([]);
  const [selected, setSelected] = useState(["AA", "DL", "UA"]);
  const [data, setData] = useState(null);
  const [disclaimer, setDisclaimer] = useState("");

  useEffect(() => {
    api.get("/fares/airlines").then((r) => {
      setAirlines(r.data.items); setAttrs(r.data.attributes); setDisclaimer(r.data.disclaimer);
    }).catch(() => {});
  }, []);

  useEffect(() => {
    if (!selected.length) { setData(null); return; }
    api.get("/fares/compare", { params: { airlines: selected.join(",") } })
      .then((r) => setData(r.data)).catch(() => setData(null));
  }, [selected]);

  const toggle = (iata) => setSelected((s) =>
    s.includes(iata) ? s.filter((x) => x !== iata) : s.length >= 3 ? [...s.slice(1), iata] : [...s, iata]);

  return (
    <div className="min-h-screen bg-zinc-50 py-10">
      <div className="max-w-6xl mx-auto px-4">
        <h1 className="font-heading font-900 text-4xl sm:text-5xl tracking-tight flex items-center gap-3">
          <Scale className="w-10 h-10 text-indigo-600" /> Fare Class Comparison
        </h1>
        <p className="text-muted-foreground mt-2 mb-6 text-sm">
          What does "Basic Economy" actually include? Compare carry-on, bags, seats, changes and refunds
          across each airline's fare families — pick up to 3 airlines.
        </p>

        <div className="flex flex-wrap gap-1.5 mb-8" data-testid="fare-airline-picker">
          {airlines.map((a) => (
            <button key={a.iata} onClick={() => toggle(a.iata)} data-testid={`fare-pick-${a.iata}`}
              className={`px-3 py-1.5 text-sm font-600 border transition-colors ${selected.includes(a.iata)
                ? "border-indigo-600 bg-indigo-600 text-white" : "border-zinc-300 bg-white text-zinc-700 hover:border-indigo-400"}`}>
              {a.name}
            </button>
          ))}
        </div>

        <div className="flex flex-wrap gap-4 mb-6 text-xs text-zinc-600">
          <span className="inline-flex items-center gap-1"><Check className="w-3.5 h-3.5 text-emerald-600" /> Included</span>
          <span className="inline-flex items-center gap-1"><CircleDollarSign className="w-3.5 h-3.5 text-amber-600" /> For a fee</span>
          <span className="inline-flex items-center gap-1"><X className="w-3.5 h-3.5 text-rose-600" /> Not included</span>
          <span className="inline-flex items-center gap-1"><Minus className="w-3.5 h-3.5 text-zinc-500" /> Limited / varies</span>
        </div>

        {data?.airlines.map((al) => (
          <div key={al.iata} className="bg-white border border-zinc-200 mb-8 overflow-x-auto" data-testid={`fare-table-${al.iata}`}>
            <div className="h-1 bg-indigo-600" />
            <div className="px-4 pt-3 pb-1 flex items-baseline gap-2">
              <h2 className="font-heading font-900 text-xl">{al.name}</h2>
              <span className="text-xs font-mono text-muted-foreground">{al.iata} · {al.region}</span>
            </div>
            <table className="w-full min-w-[720px] border-collapse">
              <thead>
                <tr className="border-b border-zinc-200">
                  <th className="text-left px-4 py-2 text-xs font-mono uppercase tracking-widest text-muted-foreground w-44">Fare attribute</th>
                  {al.fares.map((fr) => (
                    <th key={fr.name} className="text-left px-2 py-2 text-sm font-800 text-zinc-900">
                      {fr.name}
                      {fr.note && <div className="text-[10px] font-500 text-muted-foreground normal-case">{fr.note}</div>}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {attrs.map((at) => (
                  <tr key={at.key} className="border-b border-zinc-100">
                    <td className="px-4 py-2 text-xs font-600 text-zinc-600">{at.label}</td>
                    {al.fares.map((fr) => <Cell key={fr.name} a={fr.attrs[at.key]} />)}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ))}

        {selected.length === 0 && <p className="text-sm text-muted-foreground" data-testid="fare-empty">Pick at least one airline above to see its fare families.</p>}
        {disclaimer && <p className="text-xs text-muted-foreground mt-2 max-w-3xl">{disclaimer}</p>}
      </div>
    </div>
  );
}
